import React, { useEffect, useState, useRef } from 'react';
import { useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import QueueView from './QueueView.tsx';
import { QueueProps } from './QueueProps.ts';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import type { RootState } from '@/store';
import { mapInfoSelector } from 'store/map/map.selector.ts';
import { findByQuery, putDocument } from 'store/db/db.thunk.ts';
import { findFileByQuery, updateOfflineFile } from 'store/file_db/file_db.thunk.ts';
import { FileType } from 'interfaces/ia.interface.ts';
import {
  addOrDeleteFilesFunc,
  DateTimeFormat,
  mergeServerAttribution,
  prepareDMAFilesData,
  preparePAFilesData,
  saveFileToPalmettoDB,
} from '@/utilities';
import { toast } from 'react-toastify';
import { ToastMessage } from 'components/primitive';
import { ToastStatus } from '@/common/enum';
import { fetchIADataSet, saveIAData } from 'store/ia/ia.thunk.ts';
import { selectConnectionHealth } from 'store/connection/connection.selector.ts';
import { selectAuthToken, selectJwtToken } from 'store/authToken/authToken.selector.ts';
import { selectAccount } from 'store/accountsInfo/accountsInfo.selector.ts';
import { useUploadForm } from '@/hooks';
import { fetchPADataSet, savePAData } from 'store/pa/pa.thunk.ts';
import { reauthRequiredMessage } from '@/services/apiFetch.ts';

const QueueContainer: React.FC = () => {
  const navigate = useNavigate();
  const reduxStore = useStore<RootState>();
  const categorySelected = useAppSelector(mapInfoSelector).category;
  const authToken = useAppSelector(selectAuthToken);
  const palmettoUser = useAppSelector(selectAccount);
  const { uploadForm } = useUploadForm<FileType>();
  const dispatch = useAppDispatch();
  const healthRef = useRef(false);
  healthRef.current = useAppSelector(selectConnectionHealth);
  const [items, setItems] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // *: Covers the full operation, save through the post-sync dataset refresh below --
  // *: isSaving alone resets before that refresh starts, so a re-tap in that window
  // *: would race the first sync's paginated fetchIADataSet/fetchPADataSet calls and
  // *: let the slower response silently overwrite the faster one (no recency guard
  // *: on either .fulfilled reducer).
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  // *: A counter, not a flag: setting a boolean that is already true is a no-op,
  // *: so the refresh effect below would never re-run for a second sync.
  const [dataSaved, setDataSaved] = useState<number>(0);
  const [unsavedItems, setUnsavedItems] = useState(0);
  const [savedItems, setSavedItems] = useState(0);
  const [unsavedFilesForItem, setUnsavedFilesForItem] = useState(0);
  const [savedFilesForItem, setSavedFilesForItem] = useState(0);

  const goBack = () => {
    navigate(-1);
  };

  const onSyncData = async () => {
    if (healthRef.current) {
      //get all unsaved data from local db
      const localSavedData = (await findByQuery({
        selector: { isUnsaved: true, dma_category: categorySelected },
      })) as any[];
      setUnsavedItems(localSavedData.length);

      for (let i = 0; i < localSavedData.length; i++) {
        setSavedFilesForItem(0);
        const item = localSavedData[i];
        const pvGlobalID = item.pvGlobalID;
        const deletedFiles = item?.filesToDelete?.length ? item.filesToDelete : [];
        //find files associated with the globalId
        const filesArr = (await findFileByQuery({
          selector: { pvGlobalGroupID: pvGlobalID, isUnsaved: true },
        })) as any;
        setUnsavedFilesForItem(filesArr.length);

        const savedFiles: FileType[] = [];
        // Session-level preconditions: without a token or an account nothing in
        // the backlog can sync, so stopping the run is right.
        if (!authToken || !palmettoUser) return;
        // Per-item precondition. A single record missing its global ID is that
        // record's problem -- `continue` leaves the rest of the backlog to sync,
        // matching the item-level failure path below.
        if (!pvGlobalID) continue;
        //save files to palmetto and flag them as saved in local db
        for (let i = 0; i < filesArr.length; i++) {
          const file = filesArr[i];
          const binaryFile = new File([file.file], file.name, { type: file.type });
          try {
            // The ID token is read from the store at each file call rather than
            // captured when Sync was tapped: a long sync can outlive a session
            // renewal, and a captured token would be expired by then.
            await saveFileToPalmettoDB(
              binaryFile,
              selectJwtToken(reduxStore.getState()),
              uploadForm
            ).then((res) => {
              savedFiles.push(res);
              //update the file in local db to reflect that it has been saved
              file.isUnsaved = false;
              updateOfflineFile(file).then(() => {
                setSavedFilesForItem((prev) => {
                  if (prev < filesArr.length) {
                    return prev + 1;
                  } else {
                    return prev;
                  }
                });
              });
            });
          } catch {
            toast.error(
              <ToastMessage status={ToastStatus.ERROR} message={`Error saving file ${file.name}`} />
            );
          }
        }
        delete item.isUnsaved;
        const payload = {
          ...item,
          deleted: 0,
          // Null rather than 0 when there is no account to name. The server
          // overwrites this field with the id from the caller's verified ID
          // token either way, so what is sent here only ever matters locally --
          // and locally, 0 reads as a real account id that nothing wrote, while
          // null reads as what it is: the client does not know yet.
          user_id: palmettoUser?.id ?? null,
          files:
            categorySelected === 'ia'
              ? prepareDMAFilesData(savedFiles, 'ia')
              : preparePAFilesData(savedFiles),
        } as any;
        if (categorySelected === 'pa') {
          payload.date = DateTimeFormat({ dte: item.date, format: 'YYYY-MM-DD HH:mm:ss' });
        }
        try {
          await addOrDeleteFilesFunc({
            filesToAdd: savedFiles || [],
            filesToDelete: deletedFiles,
            uuid: pvGlobalID,
            jwtToken: selectJwtToken(reduxStore.getState()),
          });

          if (healthRef.current) {
            // The outcome is read off the returned action, not from a
            // try/catch: the thunks rewrap every failure as a plain Error, and
            // `await dispatch(...)` without `.unwrap()` resolves to the
            // rejected action instead of rejecting. Anything that skipped this
            // check reached `putDocument` below with `isUnsaved` deleted, which
            // drops the assessment out of the queue query permanently -- gone
            // from the device, and never saved to the server.
            //
            // The saved record is kept alongside the outcome, not just the
            // outcome: the write overwrote the body's `user_id` with the account
            // id decoded from the caller's ID token, so its response is the only
            // place this device can learn who the server recorded as the author.
            let saveFailed: boolean;
            let savedRecord: unknown;
            if (categorySelected === 'ia') {
              const saveAction = await dispatch(saveIAData(payload));
              saveFailed = saveIAData.rejected.match(saveAction);
              savedRecord = saveAction.payload;
            } else {
              const saveAction = await dispatch(savePAData(payload));
              saveFailed = savePAData.rejected.match(saveAction);
              savedRecord = saveAction.payload;
            }

            if (saveFailed) {
              // Either way the item stays queued: `isUnsaved` is left in place
              // and `putDocument` is skipped, so the next sync finds it again.
              //
              // A missing token means apiFetch ran `endSession()` inside the
              // dispatch above. That read is live store state and relies on
              // `endSession()` staying synchronous -- an `await` added inside
              // it would let this run before the resets land.
              if (!selectAuthToken(reduxStore.getState())) {
                // Nothing the loop has not reached yet can succeed either, so
                // stop here: one message, not one per remaining item.
                setIsSaving(false);
                toast.error(
                  <ToastMessage status={ToastStatus.ERROR} message={reauthRequiredMessage} />
                );
                // SPA navigation, not a page reload: a reload would unmount the
                // toast before the user could read the one message this path
                // exists to deliver.
                navigate('/login');
                return;
              }

              // The session is still good, so this is one bad item rather than
              // a dead credential -- `auth.identity.nobody`, an unreadable
              // body, a server error. Skip it and keep syncing the rest.
              toast.error(
                <ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />
              );
              continue;
            }

            setSavedItems((prev) => {
              if (prev < localSavedData.length) {
                return prev + 1;
              } else {
                return prev;
              }
            });
            delete item.isUnsaved;
            item.filesToDelete = [];
            // Adopt the server's attribution before the local write, so the
            // stored row and this copy agree on who wrote it. A `PUT` answers
            // with the driver's update result rather than the saved record, so
            // the helper leaves the local value alone when nothing came back
            // rather than replacing one guess with another.
            const updatedItem = mergeServerAttribution({ ...item }, savedRecord);
            await putDocument(updatedItem);
          } else {
            // Connectivity is gone, which is a run-level condition rather than
            // this item's problem: without the break the loop carries on
            // through the rest of the backlog, emitting one error toast per
            // remaining item plus one per file it tries to upload, after the
            // progress dialog has already closed.
            toast.error(<ToastMessage status={ToastStatus.ERROR} message='Connection Error' />);
            setIsSaving(false);
            break;
          }
        } catch (error) {
          setIsSaving(false);
          console.log(error);
          toast.error(
            <ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />
          );
        }
      }
    } else {
      toast.error(<ToastMessage status={ToastStatus.ERROR} message='Connection Error' />);
      setIsSaving(false);
    }
  };

  const handleSyncData = async () => {
    // *: Ignore taps while a sync is running; a second run would otherwise race
    // *: the first over the shared progress counters and, later, over the
    // *: post-sync dataset refresh below.
    if (isSyncing) return;
    setIsSyncing(true);
    setIsSaving(true);
    // *: Reset progress for this run -- these are module-level component state and
    // *: keep their values from the previous sync otherwise.
    setUnsavedItems(0);
    setSavedItems(0);
    setUnsavedFilesForItem(0);
    setSavedFilesForItem(0);

    try {
      await onSyncData();
    } catch (error) {
      // *: onSyncData can throw outside its own try (e.g. a PouchDB query rejecting).
      // *: Without this the exception would escape and skip everything below.
      console.log(error);
      toast.error(<ToastMessage status={ToastStatus.ERROR} message='Encountered System Error' />);
    } finally {
      // *: The modal is rendered purely from isSaving, so this must run on every exit
      // *: path. The queue items have already been pushed to Palmetto by this point.
      setIsSaving(false);
      setDataSaved((prev) => prev + 1);
    }

    // *: Best-effort refresh of the in-memory datasets, deliberately AFTER the modal
    // *: is closed: apiFetch caps each request at REQUEST_TIMEOUT_MS (30s), but
    // *: fetchIADataSet/fetchPADataSet each page through results at 500 records per
    // *: apiFetch call, so a stalled backend can hold this refresh for a duration that
    // *: scales with account size, not a fixed couple of calls.
    // *: It must never be able to hold the "Sync in progress" modal open, because
    // *: the user's data is already saved by then.
    try {
      await dispatch(fetchIADataSet());
      await dispatch(fetchPADataSet());
    } catch (error) {
      console.log('Post-sync dataset refresh failed', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const combinedProps: QueueProps = {
    goBack,
    items,
    categorySelected,
    handleSyncData,
    isSaving,
    isSyncing,
    unsavedItems,
    savedItems,
    unsavedFilesForItem,
    savedFilesForItem,
  };

  useEffect(() => {
    if (!categorySelected) return;
    const getData = async () => {
      const dataFromPouchDB = (await findByQuery({
        selector: { dma_category: categorySelected, isUnsaved: true },
      })) as any;
      setItems(dataFromPouchDB);
    };
    // *: Closing the modal is handled in handleSyncData's finally -- if it were done
    // *: here, a rejected query (or an effect that never re-runs) would strand it open.
    getData().catch((error) => {
      console.log('Failed to load queue items', error);
    });
  }, [categorySelected, dataSaved]);

  return <QueueView {...combinedProps} />;
};

export default QueueContainer;
